// validations.tsx
import { z } from "zod";

export const stringOrEmpty = (label: string) =>
  z.preprocess((val) => {
    if (typeof val === "string") return val;
    if (typeof val === "number") return String(val);

    console.warn(`[stringOrEmpty] Non-string value for "${label}":`, val);
    return "";
  }, z.string());

// Wrapper for optional fields
export const stringOrEmptyOptional = (label: string) =>
  z.preprocess((val) => {
    if (typeof val === "string") return val;
    if (typeof val === "number") return String(val);

    // coerce null/undefined/anything else to undefined
    return undefined;
  }, z.string().optional()) as z.ZodType<string | undefined>;

export const stringMandatory = (label: string) =>
  z.string({
    required_error: `${label} is required`,
    invalid_type_error: `${label} must be a string`,
  })
    .trim()
    .min(1, { message: `${label} cannot be empty` });

export const requiredString = stringMandatory;

// PAN (10 chars, uppercase alphanumeric)
export const panString = (label: string) =>
  z.preprocess((val) => {
    if (val == null) return ""; // ensure string
    if (typeof val === "string") return val.toUpperCase().trim();
    if (typeof val === "number") return String(val).toUpperCase().trim();

    console.warn(`[panString] Non-string value for "${label}":`, val);
    return "";
  },
    stringMandatory(label).regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, {
      message: `${label} must be a valid 10-character PAN (e.g. ABCDE1234F)`,
    })
  ) as z.ZodType<string>; // <- assert the type is string

// GST (15 chars, uppercase alphanumeric)
export const gstString = (label: string) =>
  z.preprocess(
    (val) => {
      if (val == null) return "";
      if (typeof val === "string") return val.toUpperCase().trim();
      if (typeof val === "number") return String(val).toUpperCase().trim();

      console.warn(`[gstString] Non-string value for "${label}":`, val);
      return "";
    },
    z
      .string()
      .refine(
        (v) => v === "" || /^[0-9A-Z]{15}$/.test(v),
        { message: `${label} must be a valid 15-character GSTIN` }
      )
  ) as z.ZodType<string>;



// Udyam Number (Mandatory)
export const udyamString = (label: string) =>
  z.preprocess(
    (val) => {
      if (val == null) return "";
      if (typeof val === "string") return val.toUpperCase().trim();
      if (typeof val === "number") return String(val).toUpperCase().trim();

      console.warn(`[udyamString] Non-string value for "${label}":`, val);
      return "";
    },
    stringMandatory(label).regex(/^[A-Z0-9-]+$/, {
      message: `${label} can only contain letters, numbers, and dashes`,
    })
  ) as z.ZodType<string>;

// Udyam Number (Optional — blank is allowed; if filled must match pattern)
export const udyamStringOptional = (label: string) =>
  z.preprocess(
    (val) => {
      if (val == null) return "";
      if (typeof val === "string") return val.toUpperCase().trim();
      if (typeof val === "number") return String(val).toUpperCase().trim();

      console.warn(`[udyamStringOptional] Non-string value for "${label}":`, val);
      return "";
    },
    z
      .string()
      .refine(
        (v) => v === "" || /^[A-Z0-9-]+$/.test(v),
        { message: `${label} can only contain letters, numbers, and dashes` }
      )
  ) as z.ZodType<string>;


// IFSC (11 chars, uppercase alphanumeric, last 6 must be alphanumeric)
export const ifscString = (label: string) =>
  z.preprocess(
    (val) => {
      if (val == null) return "";
      if (typeof val === "string") return val.toUpperCase().trim();
      if (typeof val === "number") return String(val).toUpperCase().trim();

      console.warn(`[ifscString] Non-string value for "${label}":`, val);
      return "";
    },
    z
      .string()
      .refine(
        (v) => v === "" || /^[A-Z]{4}0[0-9A-Z]{6}$/.test(v),
        { message: `${label} must be a valid 11-character IFSC code` }
      )
  );

// Bank Account Number (mandatory, numeric only, min 9 - max 18 digits)
export const bankAccountString = (label: string) =>
  z.preprocess(
    (val) => {
      if (val == null) return "";
      if (typeof val === "string") return val.trim();
      if (typeof val === "number") return String(val).trim();

      console.warn(`[bankAccountString] Non-string value for "${label}":`, val);
      return "";
    },
    z
      .string()
      .min(9, { message: `${label} must be at least 9 digits` })
      .max(18, { message: `${label} must not exceed 18 digits` })
      .refine((v) => /^[0-9]+$/.test(v), {
        message: `${label} must contain only numbers`,
      })
  );



export const dateOfBirthString = (label: string) =>
  z.preprocess((val) => {
    if (!val) return "";
    if (val instanceof Date) {
      // Format as YYYY-MM-DD in local time
      const d = val;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
    if (typeof val === "string") return val.trim();
    return "";
  },
    stringMandatory(label).refine((val) => {
      // Parse YYYY-MM-DD in local time (avoids UTC-offset shifting the date by ±1 day)
      const parts = val.split("-");
      if (parts.length !== 3) return false;
      const [year, month, day] = parts.map(Number);
      if (!year || !month || !day) return false;

      const dob = new Date(year, month - 1, day); // local midnight
      if (isNaN(dob.getTime())) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0); // local midnight

      // must not be in the future
      if (dob > today) return false;

      // must be at least 18 years old
      const eighteenthBirthday = new Date(year + 18, month - 1, day);
      return today >= eighteenthBirthday;
    }, {
      message: `${label} must be a valid date and age must be 18 or above`,
    })
  ) as z.ZodType<string>;

// Phone Number (10 digits only)
export const phoneNumberString = (label: string) =>
  stringMandatory(label).regex(/^[0-9]{10}$/, {
    message: `${label} must be a 10-digit number`,
  });

// Pincode Number (6 digits only)
export const pincodeNumberString = (label: string) =>
  stringMandatory(label).regex(/^[0-9]{6}$/, {
    message: `${label} must be a 6-digit number`,
  });

export const pincodeNumberStringOptional = (label: string) =>
  z
    .string()
    .optional()
    .refine(
      (val) => !val || /^[0-9]{6}$/.test(val),
      `${label} must be a 6-digit number`
    );

// Mandatory address: minimum 10 chars AND at least 3 words
export const addressString = (label: string) =>
  stringMandatory(label)
    .min(10, { message: `${label} must be at least 10 characters` })
    .refine(
      (val) => val.trim().split(/\s+/).length >= 3,
      { message: `${label} must contain at least 3 words` }
    );

// Optional address: if present, min 10 chars AND min 3 words
export const addressStringOptional = (label: string) =>
  z
    .string()
    .optional()
    .refine(
      (val) => !val || (val.length >= 10 && val.trim().split(/\s+/).length >= 3),
      `${label} must be at least 10 characters and contain at least 3 words`
    );




// Consent checkbox (must be true)
export const consentTrue = (label: string) =>
  z.boolean().refine((v) => v === true, {
    message: `${label} is required`,
  });


/**
 * Factory for bounded optional numbers (load-time version).
 * Empty values → `undefined`
 */
export const boundedOptionalNumberLoad = (min: number, max: number) =>
  z.preprocess(
    (val) => {
      if (typeof val === "number") return val;
      if (typeof val === "string" && val.trim() !== "" && !isNaN(Number(val))) {
        return Number(val);
      }
      return undefined; // invalid or empty -> treat as optional
    },
    z.number().optional()
  ).refine(
    (val) => val === undefined || (val >= min && val <= max),
    { message: `Value must be between ${min} and ${max} (inclusive)` }
  );

/**
 * Factory for bounded required numbers (submit-time version).
 * Must be a number in range.
 */
export const boundedNumberSubmit = (min: number, max: number) =>
  z.preprocess(
    (val) => {
      if (typeof val === "number") return val;
      if (typeof val === "string" && val.trim() !== "" && !isNaN(Number(val))) {
        return Number(val);
      }
      return undefined; // will trigger required_error
    },
    z.number({
      required_error: "This field is required",
      invalid_type_error: "This field must be a number",
    })
      .min(min, { message: `Must be ≥ ${min}` })
      .max(max, { message: `Must be ≤ ${max}` })
  );


// export const reasonForLoanSchema = z.preprocess(
// (val) => {
//   if (val === "dayToDayExpenses" || val === "newLocation" || val === "growthCapital") {
//     return val; // valid value
//   }
//   // anything else → undefined to trigger required_error
//   return undefined;
// },
// z.enum(["dayToDayExpenses", "newLocation", "growthCapital"], {
//   required_error: "Reason for loan is required",
//   invalid_type_error: "Invalid reason for loan",
// })
// );


// export const otherBusinessesSchema = z.preprocess(
//   (val) => {
//     if (val === "one" || val === "twoToThree" || val === "threeOrMore") {
//       return val; // valid value
//     }
//     // anything else → undefined to trigger required_error
//     return undefined;
//   },
//   z.enum(["one", "twoToThree", "threeOrMore"], {
//     required_error: "Other businesses selection is required",
//     invalid_type_error: "Invalid selection",
//   })
// );

export function strictEnum<T extends [string, ...string[]]>(
  options: T,
  required_error: string,
  invalid_type_error: string
) {
  return z.preprocess(
    (val) => (options.includes(val as string) ? val : undefined),
    z.enum(options, { required_error, invalid_type_error })
  );
}

export const reasonForLoanSchema = strictEnum(
  ["Working Capital"] as const,
  "Reason for loan is required",
  "Invalid reason for loan"
);

export const otherBusinessesSchema = strictEnum(
  ["zero", "one", "twoToThree", "threeOrMore"] as const,
  "Other businesses selection is required",
  "Invalid selection"
);


export function optionalEnum<T extends [string, ...string[]]>(
  allowed: T,
  invalid_type_error: string
) {
  return z.preprocess((val) => {
    if (val === null || val === undefined || val === "") return undefined; // treat as optional
    if (allowed.includes(val as string)) return val; // valid value
    return val; // pass along to Zod to throw invalid_type_error
  }, z.enum(allowed, { invalid_type_error }).optional());
}

export const optionalReasonForLoanSchema = optionalEnum(
  ["Working Capital"] as const,
  "Invalid reason for loan"
);

export const optionalOtherBusinessesSchema = optionalEnum(
  ["zero", "one", "twoToThree", "threeOrMore"] as const,
  "Invalid selection"
);